// pages/purchase-entry/VoucherTypeCombobox.tsx

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { VoucherTypeOption } from '@/stores/invoiceEntryStore';

interface VoucherTypeComboboxProps {
  value: string;
  onChange: (value: string) => void;
  voucherTypes: VoucherTypeOption[];
  placeholder?: string;
  className?: string;
}

const MAX_VISIBLE = 50;

export function VoucherTypeCombobox({
  value,
  onChange,
  voucherTypes,
  placeholder = 'Search voucher type...',
  className,
}: VoucherTypeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter voucher types based on search
  const filteredTypes = useMemo(() => {
    if (!search || search.length < 2) return [];
    const query = search.toLowerCase();
    return voucherTypes
      .filter((t) => t.name?.toLowerCase().includes(query))
      .slice(0, MAX_VISIBLE);
  }, [search, voucherTypes]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (typeName: string) => {
    onChange(typeName);
    setSearch('');
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
    setSearch('');
  };

  const displayValue = open ? search : value;

  return (
    <div ref={containerRef} className={cn('relative', className)} title={value}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
        <input
          type="text"
          value={displayValue}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleFocus}
          placeholder={open ? `Search: ${value || placeholder}` : value || placeholder}
          className={cn(
            'w-full h-8 pl-7 pr-8 text-sm rounded-md border border-input bg-background',
            'focus:outline-none focus:ring-1 focus:ring-ring',
            'text-foreground truncate',
            open && !search && 'placeholder:text-foreground/70'
          )}
          title={value}
        />
        <ChevronsUpDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
      </div>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {search.length < 2 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              Type 2+ characters to search...
            </div>
          ) : filteredTypes.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No voucher types found
            </div>
          ) : (
            <>
              {filteredTypes.map((voucherType) => (
                <div
                  key={voucherType.name}
                  onClick={() => handleSelect(voucherType.name)}
                  className={cn(
                    'flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-accent',
                    value === voucherType.name && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'w-3 h-3 mr-2 flex-shrink-0',
                      value === voucherType.name ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{voucherType.name}</span>
                  {voucherType.parent && (
                    <span className="ml-auto text-xs text-muted-foreground truncate max-w-20">
                      {voucherType.parent}
                    </span>
                  )}
                </div>
              ))}
              {filteredTypes.length === MAX_VISIBLE && (
                <div className="p-1 text-xs text-muted-foreground text-center border-t">
                  Showing first {MAX_VISIBLE} matches...
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
